/**
 * Component: Collapsible Block
 * -----------------------------------------------------------------------------
 * - A responsive, collapsible component with optional mobile-only behavior
 * - A WCAG-compliant, keyboard-accessible accordion Web Component
 * - This is intended to be imported into a given section, followed by
 * customElements.define('...')
 * @namespace CollapsibleBlock
 * @example
 * <collapsible-block data-only-one-open="false" data-mobile-only="false">
 *   <div data-js-accordion-section>
 *     <button
 *       id="header-1"
 *       data-js-accordion-trigger
 *       aria-controls="panel-1"
 *       aria-expanded="false"
 *     >
 *       Header Text
 *     </button>
 *     <div
 *       id="panel-1"
 *       data-js-accordion-panel
 *       role="region"
 *       aria-labelledby="header-1"
 *       aria-hidden="true"
 *     >
 *       Content Text
 *     </div>
 *   </div>
 * </collapsible-block>
 */
import breakpoints from '~/helpers/breakpoints'
import { parseAttributeValue } from '~/helpers/dom'

/**
 * Create CollapsibleBlock Class.
 */
export default class CollapsibleBlock extends HTMLElement {
  static observedAttributes = [ 'data-mobile-only' ]

  constructor() {
    super()

    this.selectors = {
      section: '[data-js-accordion-section]',
      trigger: '[data-js-accordion-trigger]',
      panel: '[data-js-accordion-panel]',
    }

    this.sections = []

    this.setDefaultState()
  }

  /**
   * Set the default state.
   */
  setDefaultState() {
    this.mobileOnly = parseAttributeValue(
      this.getAttribute('data-mobile-only'),
    )
    this.isMobile = !window.matchMedia(`(min-width: ${breakpoints.large})`)
      .matches
    this.allowOnlyOne = this.getAttribute('data-only-one-open') === 'true'
  }

  /**
   * The element is now defined on the page, so run functionality.
   */
  connectedCallback() {
    this.setupAccordionSections()
    this.updateOpenState()
    this.setEventListeners()
  }

  /**
   * Set Event Listeners.
   */
  setEventListeners() {
    if (!this.sections.length) {
      return
    }

    this.sections.forEach(({ trigger, panel }, index) => {
      trigger.addEventListener('click', () => {
        this.toggle(trigger, panel)
      })

      trigger.addEventListener('keydown', (event) => {
        this.handleKeydown(event, index)
      })
    })

    if (!this.mobileOnly) {
      return
    }

    /**
     * Screen has been resized.
     */
    mint.EventBus.on('mint:resize', (response) => {
      this.isMobile = response.screen?.mobile
      this.updateOpenState()
    })
  }

  /**
   * Gathers and validates all accordion sections and elements.
   */
  setupAccordionSections() {
    const rawSections = this.querySelectorAll(this.selectors.section)

    if (!rawSections.length) {
      return
    }

    this.sections = Array.from(rawSections)
      .map((section) => {
        const trigger = section.querySelector(this.selectors.trigger)
        const panel = section.querySelector(this.selectors.panel)

        if (!trigger || !panel) {
          return false
        }

        return { section, trigger, panel }
      })
      .filter(Boolean)
  }

  /**
   * Toggles the state of a given trigger and panel.
   * @param {HTMLButtonElement} trigger - The button element to toggle.
   * @param {HTMLElement} panel - The content panel controlled by the trigger.
   */
  toggle(trigger, panel) {
    if (!trigger || !panel) {
      return
    }

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

    if (!isExpanded && this.allowOnlyOne) {
      this.closeAll()
    }

    if (isExpanded) {
      this.collapse(trigger, panel)
    } else {
      this.expand(trigger, panel)
    }
  }

  /**
   * Expands a given accordion panel by transitioning its height.
   * @param {HTMLButtonElement} trigger - The button element to toggle.
   * @param {HTMLElement} panel - The panel to expand.
   */
  expand(trigger, panel) {
    if (!trigger || !panel) {
      return
    }

    // Ensure panel is visible before measurement
    panel.style.height = 'auto'
    const fullHeight = panel.scrollHeight
    panel.style.height = '0px'

    requestAnimationFrame(() => {
      panel.style.height = `${fullHeight}px`
      panel.setAttribute('aria-hidden', 'false')
      trigger.setAttribute('aria-expanded', 'true')
    })
  }

  /**
   * Collapses a given accordion panel by transitioning its height to 0.
   * @param {HTMLButtonElement} trigger - The button element to toggle.
   * @param {HTMLElement} panel - The panel to collapse.
   */
  collapse(trigger, panel) {
    if (!trigger || !panel) {
      return
    }

    const fullHeight = panel.scrollHeight
    panel.style.height = `${fullHeight}px`

    requestAnimationFrame(() => {
      panel.style.height = '0px'
      panel.setAttribute('aria-hidden', 'true')
      trigger.setAttribute('aria-expanded', 'false')
    })
  }

  /**
   * Handles keyboard navigation on accordion triggers.
   * @param {KeyboardEvent} event - Keyboard event from trigger.
   * @param {number} currentIndex - The index of the current trigger.
   */
  handleKeydown(event, currentIndex) {
    const triggers = this.sections.map((section) => section.trigger)

    if (!triggers.length) {
      return
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        triggers[(currentIndex + 1) % triggers.length]?.focus()

        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        triggers[
          (currentIndex - 1 + triggers.length) % triggers.length
        ]?.focus()

        break
      }
      case 'Home': {
        event.preventDefault()
        triggers[0]?.focus()

        break
      }
      case 'End': {
        event.preventDefault()
        triggers[triggers.length - 1]?.focus()

        break
      }
    }
  }

  /**
   * Closes all accordion sections by collapsing each panel and resetting
   * `aria-*` attributes.
   */
  closeAll() {
    this.sections.forEach(({ trigger, panel }, index) =>
      this.collapse(trigger, panel),
    )
  }

  /**
   * Update the accordion state based on the screen size.
   * - set `open` state for the accordions on desktop if `data-mobile-only` is set.
   */
  updateOpenState() {
    if (!this.mobileOnly) {
      return
    }

    this.isMobile
      ? this.closeAll()
      : this.sections.forEach(({ trigger, panel }, index) =>
        this.expand(trigger, panel),
      )
  }
}